/**
 * Pure logic for the create-program form.
 *
 * Small, but the session-array construction genuinely needs direct testing:
 * the previous code did `Array(parseInt(numberOfSessions)).fill()`, and
 * `parseInt('')` is `NaN`, so clearing the field made `Array(NaN)` throw
 * `RangeError: Invalid array length`.
 */

/**
 * How many sessions the form will let you create at once.
 *
 * This is a **UI guard, not an API rule**. The backend enforces no cap on
 * sessions per program — see docs/specs/api-contract.md §7. Fourteen is two
 * weeks of daily training, past which you are better served adding sessions one
 * at a time in the program editor.
 */
export const MIN_SESSIONS = 1;
export const MAX_SESSIONS = 14;

export const EMPTY_PROGRAM_FORM = {
  name: '',
  description: '',
  numberOfSessions: '3',
};

const asText = (value) => String(value ?? '').trim();

/** Validate the form. Returns an object keyed by field; empty when valid. */
export function validateProgramForm(form = {}) {
  const errors = {};

  if (asText(form.name) === '') {
    errors.name = 'Give the program a name.';
  }

  // Accepts a leading minus so that "-2" is range-checked rather than reported
  // as "not a whole number", which it plainly is.
  const raw = asText(form.numberOfSessions);
  if (raw === '' || !/^-?\d+$/.test(raw)) {
    errors.numberOfSessions = 'Number of sessions must be a whole number.';
  } else {
    const count = Number(raw);
    if (count < MIN_SESSIONS || count > MAX_SESSIONS) {
      errors.numberOfSessions = `Number of sessions must be between ${MIN_SESSIONS} and ${MAX_SESSIONS}.`;
    }
  }

  return errors;
}

/**
 * Build the POST body for creating a program.
 *
 * Sessions are created empty and named positionally; exercises are added
 * afterwards in the program editor. `order` is 1-based, matching
 * CreateSessionRequest's `min=1` constraint.
 *
 * @returns {{body: object}|{errors: object}}
 */
export function buildProgramRequest(form) {
  const errors = validateProgramForm(form);
  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  const count = Number(asText(form.numberOfSessions));

  return {
    body: {
      name: asText(form.name),
      description: asText(form.description),
      sessions: Array.from({ length: count }, (_, index) => ({
        name: `Session ${index + 1}`,
        order: index + 1,
        exercises: [],
      })),
    },
  };
}

/**
 * Sort program summaries newest first.
 *
 * Copies before sorting: the previous code called `.sort()` directly on the
 * axios response array, mutating it in place.
 *
 * Reads only `createdAt`, the field on ProgramSummaryResponse. The old untagged
 * response spelled it `CreatedAt`, but backend and frontend ship in one image,
 * so there is no version skew to tolerate — a fallback here would be permanent
 * dead weight encoding a transition that lasted one commit.
 */
export function sortProgramsByNewest(programs) {
  const createdAt = (program) => {
    const time = program?.createdAt ? new Date(program.createdAt).getTime() : NaN;
    return Number.isNaN(time) ? 0 : time;
  };

  return [...(programs ?? [])].sort((a, b) => createdAt(b) - createdAt(a));
}
